import moment from 'moment';

/**
 * Helper function to get month name from month number.
 * @param {number} monthNumber - The month number (1-12).
 * @returns {string} The abbreviated month name (e.g., 'Jan', 'Feb').
 */
const getMonthName = (monthNumber) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  // Ensure monthNumber is within a valid range
  if (monthNumber >= 1 && monthNumber <= 12) {
    return months[monthNumber - 1];
  }
  console.warn('Invalid month number provided to getMonthName:', monthNumber);
  return 'Unknown';
};




/**
 * Calculates quarter information for a given date based on fiscal year settings.
 * @param {string|Date|moment.Moment} date - The date to calculate the quarter for.
 * @param {object} financeSettings - Object containing finance settings.
 * @param {string} [financeSettings.finance_fiscal_year_start_month='1'] - The starting month of the fiscal year (1-12).
 * @param {string} [financeSettings.finance_months_per_quarter='3'] - Number of months per quarter.
 * @returns {object} An object containing quarterKey, quarterLabel, fiscalYear, and quarter details.
 */
export const getQuarterFromDate = (date, financeSettings = {}) => {
  try {
    const momentDate = moment(date);

    // Validate if the provided date is a valid moment object
    if (!momentDate.isValid()) {
      console.warn('Invalid date provided to getQuarterFromDate:', date);
      return {
        quarterKey: 'unknown',
        quarterLabel: 'Unknown',
        fiscalYear: new Date().getFullYear(), // Default to current year
        quarter: { name: 'Unknown', number: 0, startMonth: 1, endMonth: 12, startMonthName: 'Jan', endMonthName: 'Dec' }
      };
    }

    // Parse fiscal year settings, defaulting to standard calendar year if not provided or invalid
    const fiscalYearStartMonth = parseInt(financeSettings?.finance_fiscal_year_start_month || '1') - 1; // 0-based
    const monthsPerQuarter = parseInt(financeSettings?.finance_months_per_quarter || '3');

    // Basic validation for parsed settings
    if (isNaN(fiscalYearStartMonth) || fiscalYearStartMonth < 0 || fiscalYearStartMonth > 11) {
      console.warn('Invalid finance_fiscal_year_start_month in settings, defaulting to January.');
      financeSettings.finance_fiscal_year_start_month = '1';
    }
    if (isNaN(monthsPerQuarter) || monthsPerQuarter <= 0 || monthsPerQuarter > 12 || (12 % monthsPerQuarter !== 0)) {
      console.warn('Invalid finance_months_per_quarter in settings, defaulting to 3.');
      financeSettings.finance_months_per_quarter = '3';
    }

    // Recalculate with potentially corrected defaults
    const effectiveFiscalYearStartMonth = parseInt(financeSettings?.finance_fiscal_year_start_month || '1') - 1;
    const effectiveMonthsPerQuarter = parseInt(financeSettings?.finance_months_per_quarter || '3');

    // Calculate fiscal year based on the fiscal year start month
    let fiscalYear = momentDate.year();
    if (momentDate.month() < effectiveFiscalYearStartMonth) {
      fiscalYear = fiscalYear - 1;
    }

    // Calculate month's position within the fiscal year (0-indexed)
    const monthInFiscalYear = (momentDate.month() - effectiveFiscalYearStartMonth + 12) % 12;
    // Calculate quarter number (1-indexed)
    const quarterNumber = Math.floor(monthInFiscalYear / effectiveMonthsPerQuarter) + 1;
    const quarterName = `Q${quarterNumber}`;

    // Calculate start and end months for the specific quarter within the fiscal year
    const quarterStartMonthAbsolute = effectiveFiscalYearStartMonth + ((quarterNumber - 1) * effectiveMonthsPerQuarter);
    const quarterEndMonthAbsolute = effectiveFiscalYearStartMonth + (quarterNumber * effectiveMonthsPerQuarter) - 1;

    // Convert absolute month indices to 1-based month numbers (0=Jan, 11=Dec)
    const startMonth = (quarterStartMonthAbsolute % 12) + 1;
    const endMonth = (quarterEndMonthAbsolute % 12) + 1;

    // Get month names
    const startMonthName = getMonthName(startMonth);
    const endMonthName = getMonthName(endMonth);

    return {
      quarterKey: `${fiscalYear}-${quarterName}`,
      // Updated quarterLabel to include the fiscal year
      quarterLabel: `${quarterName} (${startMonthName}-${endMonthName}) ${fiscalYear}`,
      fiscalYear,
      quarter: {
        name: quarterName,
        number: quarterNumber,
        startMonth: startMonth,
        endMonth: endMonth,
        startMonthName,
        endMonthName
      }
    };
  } catch (error) {
    console.error('Error in getQuarterFromDate:', error);
    return {
      quarterKey: 'error',
      quarterLabel: 'Error',
      fiscalYear: new Date().getFullYear(),
      quarter: { name: 'Error', number: 0, startMonth: 1, endMonth: 12, startMonthName: 'Jan', endMonthName: 'Dec' }
    };
  }
};



/**
 * Provides a quarter calculator function that uses the given finance settings.
 * This function is designed to be called once to get a calculator,
 * and the calculator function can then be reused with different dates.
 * @param {object} financeSettings - The finance settings to use for quarter calculations.
 * @returns {function(string|Date|moment.Moment): object} A function that takes a date and returns its quarter info.
 */
export const getQuarterCalculator = (financeSettings = {}) => {
  // Return a function that is pre-configured with the financeSettings
  return (date) => getQuarterFromDate(date, financeSettings);
};



/**
 * Helper function to get all quarters for a given fiscal year.
 * @param {number} year - The fiscal year to get quarters for.
 * @param {object} financeSettings - Object containing finance settings.
 * @param {string} [financeSettings.finance_fiscal_year_start_month='1'] - The starting month of the fiscal year (1-12).
 * @param {string} [financeSettings.finance_months_per_quarter='3'] - Number of months per quarter.
 * @returns {Array<object>} An array of quarter objects for the specified year.
 */
export const getQuartersForYear = (year, financeSettings = {}) => {
  if (typeof year !== 'number' || isNaN(year)) {
    console.error('Invalid year provided to getQuartersForYear:', year);
    return [];
  }

  const fiscalYearStartMonth = parseInt(financeSettings?.finance_fiscal_year_start_month || '1') - 1;
  const monthsPerQuarter = parseInt(financeSettings?.finance_months_per_quarter || '3');

  // Basic validation for parsed settings
  if (isNaN(fiscalYearStartMonth) || fiscalYearStartMonth < 0 || fiscalYearStartMonth > 11) {
    console.warn('Invalid finance_fiscal_year_start_month in settings, defaulting to January.');
    financeSettings.finance_fiscal_year_start_month = '1';
  }
  if (isNaN(monthsPerQuarter) || monthsPerQuarter <= 0 || monthsPerQuarter > 12 || (12 % monthsPerQuarter !== 0)) {
    console.warn('Invalid finance_months_per_quarter in settings, defaulting to 3.');
    financeSettings.finance_months_per_quarter = '3';
  }

  const effectiveFiscalYearStartMonth = parseInt(financeSettings?.finance_fiscal_year_start_month || '1') - 1;
  const effectiveMonthsPerQuarter = parseInt(financeSettings?.finance_months_per_quarter || '3');

  const totalQuarters = Math.ceil(12 / effectiveMonthsPerQuarter);

  const quarters = [];
  for (let i = 1; i <= totalQuarters; i++) {
    const quarterName = `Q${i}`;
    const quarterStartMonthAbsolute = effectiveFiscalYearStartMonth + ((i - 1) * effectiveMonthsPerQuarter);
    const quarterEndMonthAbsolute = effectiveFiscalYearStartMonth + (i * effectiveMonthsPerQuarter) - 1;

    const startMonth = (quarterStartMonthAbsolute % 12) + 1;
    const endMonth = (quarterEndMonthAbsolute % 12) + 1;

    const startMonthName = getMonthName(startMonth);
    const endMonthName = getMonthName(endMonth);

    quarters.push({
      quarterKey: `${year}-${quarterName}`,
      // Updated quarterLabel to include the fiscal year
      quarterLabel: `${quarterName} (${startMonthName}-${endMonthName}) ${year}`,
      fiscalYear: year,
      quarter: {
        name: quarterName,
        number: i,
        startMonth: startMonth,
        endMonth: endMonth,
        startMonthName,
        endMonthName
      }
    });
  }

  return quarters;
};



/**
 * Checks if a given date falls within a specific quarter.
 * @param {string|Date|moment.Moment} date - The date to check.
 * @param {string} quarterKey - The quarter key (e.g., '2024-Q1').
 * @param {object} financeSettings - Object containing finance settings.
 * @returns {boolean} True if the date is in the specified quarter, false otherwise.
 */
export const isDateInQuarter = (date, quarterKey, financeSettings = {}) => {
  if (!quarterKey || typeof quarterKey !== 'string') {
    console.error('Invalid quarterKey provided to isDateInQuarter:', quarterKey);
    return false;
  }
  const quarterInfo = getQuarterFromDate(date, financeSettings);
  return quarterInfo.quarterKey === quarterKey;
};




/**
 * Gets the start and end dates for a given quarter key based on fiscal year settings.
 * @param {string} quarterKey - The quarter key (e.g., '2024-Q1').
 * @param {object} financeSettings - Object containing finance settings.
 * @param {string} [financeSettings.finance_fiscal_year_start_month='1'] - The starting month of the fiscal year (1-12).
 * @param {string} [financeSettings.finance_months_per_quarter='3'] - Number of months per quarter.
 * @returns {object} An object with startDate and endDate in 'YYYY-MM-DD' format.
 */
export const getQuarterDateRange = (quarterKey, financeSettings = {}) => {
  if (!quarterKey || typeof quarterKey !== 'string' || !quarterKey.includes('-Q')) {
    console.error('Invalid quarterKey format provided to getQuarterDateRange:', quarterKey);
    return { startDate: null, endDate: null };
  }

  const [yearStr, quarterName] = quarterKey.split('-');
  const year = parseInt(yearStr);
  const quarterNumber = parseInt(quarterName.replace('Q', ''));

  if (isNaN(year) || isNaN(quarterNumber) || quarterNumber < 1 || quarterNumber > 4) { // Assuming max 4 quarters for simplicity
    console.error('Invalid year or quarter number in quarterKey:', quarterKey);
    return { startDate: null, endDate: null };
  }

  const fiscalYearStartMonth = parseInt(financeSettings?.finance_fiscal_year_start_month || '1') - 1; // 0-based
  const monthsPerQuarter = parseInt(financeSettings?.finance_months_per_quarter || '3');

  // Basic validation for parsed settings
  if (isNaN(fiscalYearStartMonth) || fiscalYearStartMonth < 0 || fiscalYearStartMonth > 11) {
    console.warn('Invalid finance_fiscal_year_start_month in settings, defaulting to January.');
    financeSettings.finance_fiscal_year_start_month = '1';
  }
  if (isNaN(monthsPerQuarter) || monthsPerQuarter <= 0 || monthsPerQuarter > 12 || (12 % monthsPerQuarter !== 0)) {
    console.warn('Invalid finance_months_per_quarter in settings, defaulting to 3.');
    financeSettings.finance_months_per_quarter = '3';
  }

  const effectiveFiscalYearStartMonth = parseInt(financeSettings?.finance_fiscal_year_start_month || '1') - 1;
  const effectiveMonthsPerQuarter = parseInt(financeSettings?.finance_months_per_quarter || '3');

  const startMonthAbsolute = effectiveFiscalYearStartMonth + ((quarterNumber - 1) * effectiveMonthsPerQuarter);
  const endMonthAbsolute = effectiveFiscalYearStartMonth + (quarterNumber * effectiveMonthsPerQuarter) - 1;

  // Calculate the actual calendar year for the start and end months
  let startCalYear = year;
  if (startMonthAbsolute < effectiveFiscalYearStartMonth && startMonthAbsolute < 0) { // If start month wraps into previous calendar year
    startCalYear = year + Math.floor(startMonthAbsolute / 12);
  } else if (startMonthAbsolute >= 12 && effectiveFiscalYearStartMonth !== 0) { // If fiscal year starts mid-year and quarter wraps into next calendar year
    startCalYear = year + Math.floor(startMonthAbsolute / 12);
  }

  let endCalYear = year;
  if (endMonthAbsolute < effectiveFiscalYearStartMonth && endMonthAbsolute < 0) { // If end month wraps into previous calendar year
    endCalYear = year + Math.floor(endMonthAbsolute / 12);
  } else if (endMonthAbsolute >= 12 && effectiveFiscalYearStartMonth !== 0) { // If fiscal year starts mid-year and quarter wraps into next calendar year
    endCalYear = year + Math.floor(endMonthAbsolute / 12);
  }


  const startDate = moment(`${startCalYear}-${(startMonthAbsolute % 12) + 1}-01`, 'YYYY-M-D');
  const endDate = moment(`${endCalYear}-${(endMonthAbsolute % 12) + 1}-01`, 'YYYY-M-D').endOf('month');

  // Final check for validity
  if (!startDate.isValid() || !endDate.isValid()) {
    console.error('Calculated start or end date is invalid for quarterKey:', quarterKey);
    return { startDate: null, endDate: null };
  }

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD')
  };
};